interface ServerTimeResponse {
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
  milliseconds: number;
  seconds: number;
  nanoseconds: number;
}

class TimeSyncService {
  private offsetSeconds: number = 0;
  private isInitialized: boolean = false;
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private retryCount: number = 0;
  private maxRetries: number = 3;
  private retryDelayMs: number = 1000;

  /**
   * Initialize time sync with server
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    return this.fetchServerTimeWithRetry();
  }

  /**
   * Fetch server time and calculate offset with retry logic
   */
  private async fetchServerTimeWithRetry(): Promise<boolean> {
    try {
      // Clear any existing retry timeout
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
        this.retryTimeoutId = null;
      }

      const startTimeSeconds = Math.floor(Date.now() / 1000);
      const serverTimeResponse = await this.fetchServerTime();
      const endTimeSeconds = Math.floor(Date.now() / 1000);
      
      // Calculate network delay and adjust server time
      const networkDelaySeconds = (endTimeSeconds - startTimeSeconds) / 2;
      const adjustedServerTimeSeconds = serverTimeResponse.seconds + networkDelaySeconds;
      
      // Store offset (server time - client time)
      this.offsetSeconds = adjustedServerTimeSeconds - endTimeSeconds;
      this.isInitialized = true;
      this.retryCount = 0;
      
      console.log(`[TimeSync] Synchronized. Offset: ${this.offsetSeconds}s`);
      return true;
      
    } catch (error) {
      console.error('[TimeSync] Failed to fetch server time:', error);
      
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        const delay = this.retryDelayMs * Math.pow(2, this.retryCount - 1); // Exponential backoff
        
        console.log(`[TimeSync] Retrying in ${delay}ms... (${this.retryCount}/${this.maxRetries})`);
        
        return new Promise((resolve) => {
          this.retryTimeoutId = setTimeout(async () => {
            const success = await this.fetchServerTimeWithRetry();
            resolve(success);
          }, delay);
        });
      }
      
      // Max retries reached, fallback to client time
      console.warn('[TimeSync] Max retries reached, using client time');
      this.offsetSeconds = 0;
      this.isInitialized = true;
      return false;
    }
  }

  /**
   * Fetch server time from API
   */
  private async fetchServerTime(): Promise<ServerTimeResponse> {
    const { default: apiClient } = await import('./api');
    return await apiClient.getServerTime();
  }

  /**
   * Get synchronized server time, fallback to client time
   */
  getServerTime(): number {
    return Math.floor(Date.now() / 1000) + this.offsetSeconds;
  }

  /**
   * Get synchronized server time in seconds (alias for getServerTime)
   */
  getServerTimeSeconds(): number {
    return this.getServerTime();
  }

  /**
   * Check if time sync is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current offset
   */
  getOffset(): number {
    return this.offsetSeconds;
  }

  /**
   * Force sync with server (for compatibility)
   */
  async forceSync(): Promise<boolean> {
    this.isInitialized = false;
    this.retryCount = 0;
    return this.fetchServerTimeWithRetry();
  }

  /**
   * Get time since last sync (simplified - always returns 0 for compatibility)
   */
  getTimeSinceLastSync(): number {
    return 0;
  }

  /**
   * Reset the service
   */
  reset() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    this.offsetSeconds = 0;
    this.isInitialized = false;
    this.retryCount = 0;
  }

  /**
   * Legacy compatibility methods
   */
  onInitialized(callback: (isInitialized: boolean) => void) {
    // Simplified - call immediately with current state
    callback(this.isInitialized);
  }

  removeListener(callback: (isInitialized: boolean) => void) {
    // No-op for simplified implementation
  }
}

// Create singleton instance
const timeSyncService = new TimeSyncService();

export default timeSyncService;
export type { ServerTimeResponse };
